import { useMemo } from 'react'
import { Spinner } from '@heroui/react'
import { StationPanelLinePhoto } from '../panels/StationPanelLinePhoto'
import { YearRangePicker } from './shared/YearRangePicker'
import { YlyStatsGrid } from './shared/YlyStatsGrid'
import { TableOrientationToggle } from './shared/TableOrientationToggle'
import { withShortLabels } from './shared/rowGroupsTransposed'
import { useYearlyData } from '../../hooks/useYearlyData'
import { useYearlyNormals } from '../../hooks/useYearlyNormals'
import { useYearRangeStore } from '../../store/useYearRangeStore'
import { useTableOrientationStore } from '../../store/useTableOrientationStore'

const SLUG = 'yly-prehled'

const ROW_GROUPS = [
  {
    label: 'Teplota vzduchu',
    rows: [
      { tsId: 'TEMPAVG_AVG', label: 'Průměrná roční teplota',       unit: '°C',   normalMode: 'diff', decimals: 1 },
      { tsId: 'TXMAX_MAX',   label: 'Roční maximum denních maxim',   unit: '°C',   normalMode: 'diff', decimals: 1 },
      { tsId: 'TNMIN_MIN',   label: 'Roční minimum denních minim',   unit: '°C',   normalMode: 'diff', decimals: 1 },
    ],
  },
  {
    label: 'Teplotní dny',
    rows: [
      { tsId: 'TXMAX_GE25', label: 'Letní dny – Tmax ≥ 25 °C',   unit: 'dny', normalMode: 'diff', decimals: 0 },
      { tsId: 'TXMAX_GE30', label: 'Horké dny – Tmax ≥ 30 °C',    unit: 'dny', normalMode: 'diff', decimals: 0 },
      { tsId: 'TNMIN_L0',   label: 'Mrazové dny – Tmin < 0 °C',   unit: 'dny', normalMode: 'diff', decimals: 0 },
    ],
  },
  {
    label: 'Srážky & sníh',
    rows: [
      { tsId: 'PRECIPSUM_SUM', label: 'Roční srážkový úhrn',          unit: 'mm',  normalMode: 'pct',  decimals: 1 },
      { tsId: 'PRECIPSUM_MAX', label: 'Denní maximum srážek',          unit: 'mm',  normalMode: null,   decimals: 1 },
      { tsId: 'PRECIPSUM_G0',  label: 'Dny se srážkami ≥ 0.1 mm',     unit: 'dny', normalMode: 'diff', decimals: 0 },
      { tsId: 'SNOW_MAX',      label: 'Maximum sněhové pokrývky',      unit: 'cm',  normalMode: null,   decimals: 0 },
      { tsId: 'SNOW_GE1',      label: 'Dny se sněhem ≥ 1 cm',          unit: 'dny', normalMode: 'diff', decimals: 0 },
    ],
  },
  {
    label: 'Sluneční svit & záření',
    rows: [
      { tsId: 'SUNSHINESUM_SUM',  label: 'Délka slunečního svitu', unit: 'hod',    normalMode: 'pct', decimals: 1 },
      { tsId: 'GLOBALRADSUM_SUM', label: 'Globální záření',         unit: 'kJ/m²', normalMode: 'pct', decimals: 0 },
    ],
  },
  {
    label: 'Vítr',
    rows: [
      { tsId: 'WSAVG_AVG', label: 'Průměrná rychlost větru', unit: 'm/s', normalMode: 'diff', decimals: 1 },
      { tsId: 'WSMAX_MAX', label: 'Maximum nárazu větru',     unit: 'm/s', normalMode: 'diff', decimals: 1 },
    ],
  },
]

// 2. definice hlavičky – transponovaná tabulka (roky = řádky, veličiny = úzké sloupce).
const ROW_GROUPS_T = withShortLabels(ROW_GROUPS, {
  TEMPAVG_AVG: 'Prům. teplota', TXMAX_MAX: 'Max. denních max.', TNMIN_MIN: 'Min. denních min.',
  TXMAX_GE25: 'Letní ≥ 25', TXMAX_GE30: 'Horké ≥ 30', TNMIN_L0: 'Mrazové < 0',
  PRECIPSUM_SUM: 'Úhrn', PRECIPSUM_MAX: 'Denní max.', PRECIPSUM_G0: 'Dny ≥ 0.1 mm',
  SNOW_MAX: 'Max. sníh', SNOW_GE1: 'Dny sníh ≥ 1 cm',
  SUNSHINESUM_SUM: 'Délka svitu', GLOBALRADSUM_SUM: 'Glob. záření',
  WSAVG_AVG: 'Prům. rychlost', WSMAX_MAX: 'Max. náraz',
}, {
  'Teplota vzduchu': 'Teploty', 'Srážky & sníh': 'Srážky & sníh',
  'Sluneční svit & záření': 'Svit & záření',
})

const TS_IDS = ROW_GROUPS.flatMap(g => g.rows.map(r => r.tsId))

export function YlyPrehledView({ station }) {
  const { yearFrom, yearTo, setYearFrom, setYearTo } = useYearRangeStore()
  const orientation = useTableOrientationStore((s) => s.getOrientation(SLUG))

  const years = useMemo(
    () => Array.from({ length: yearTo - yearFrom + 1 }, (_, i) => String(yearFrom + i)),
    [yearFrom, yearTo]
  )

  const { dataMap, isLoading: dLoading, isError: dError } = useYearlyData(station?.gh_id, yearFrom, yearTo, TS_IDS)
  const { normalsMap, isLoading: nLoading }               = useYearlyNormals(station?.gh_id, TS_IDS)

  const isLoading = dLoading || nLoading

  return (
    <div className="flex flex-col h-full">
      <div className="p-4 border-b border-gray-200 bg-white flex flex-col gap-3">
        {station && <StationPanelLinePhoto station={station} />}
        <div className="flex items-center justify-between gap-3">
          <YearRangePicker yearFrom={yearFrom} yearTo={yearTo} onYearFromChange={setYearFrom} onYearToChange={setYearTo} />
          <TableOrientationToggle slug={SLUG} />
        </div>
      </div>
      <div className="flex-1 min-h-0 p-4 flex flex-col">
        {isLoading && <div className="flex items-center gap-2 text-sm text-gray-500"><Spinner size="sm" /> Načítání dat…</div>}
        {dError && <p className="text-red-500 text-sm">Chyba při načítání dat</p>}
        {!isLoading && !dError && (
          <YlyStatsGrid
            rowGroups={ROW_GROUPS}
            rowGroupsTransposed={ROW_GROUPS_T}
            orientation={orientation}
            dataMap={dataMap}
            normalsMap={normalsMap}
            years={years}
          />
        )}
      </div>
    </div>
  )
}
